/**
 * Permission Constants
 *
 * Source of truth: DRT backend (drt_ui/trunk/config/user_permissions.yml
 *                              + drt_ui/trunk/config/user_permission_groups.yml)
 *
 * These strings are exactly what the DRT REST API returns in the session
 * `permissions` array (GET /api/session → { permissions: [...] }).
 *
 * Rules:
 *  - One constant per permission — no aliases, no duplicates
 *  - UPPER_SNAKE_CASE key, snake_case string value
 *  - Values are the canonical DRT permission codes
 */

export const PERMISSIONS = {

  // ─── Alert ───────────────────────────────────────────────────────────────
  ALERT_UPDATE: 'alert_update',
  ALERT_VIEW: 'alert_view',

  // ─── Allowed Value Set ────────────────────────────────────────────────────
  ALLOWED_VALUE_SET_CREATE: 'allowed_value_set_create',
  ALLOWED_VALUE_SET_DELETE: 'allowed_value_set_delete',
  ALLOWED_VALUE_SET_UPDATE: 'allowed_value_set_update',
  ALLOWED_VALUE_SET_VIEW: 'allowed_value_set_view',

  // ─── Archive Inspection Checklist ─────────────────────────────────────────
  ARCHIVE_INSPECTION_CHECKLIST_CREATE: 'archive_inspection_checklist_create',
  ARCHIVE_INSPECTION_CHECKLIST_DELETE: 'archive_inspection_checklist_delete',
  ARCHIVE_INSPECTION_CHECKLIST_UPDATE: 'archive_inspection_checklist_update',
  ARCHIVE_INSPECTION_CHECKLIST_VIEW: 'archive_inspection_checklist_view',
  LINK_ROUTE_OPERATION: 'link_route_operation',

  // ─── Archive Route ────────────────────────────────────────────────────────
  ARCHIVE_ROUTE_CREATE: 'archive_route_create',
  ARCHIVE_ROUTE_DELETE: 'archive_route_delete',
  ARCHIVE_ROUTE_UPDATE: 'archive_route_update',
  ARCHIVE_ROUTE_VIEW: 'archive_route_view',

  // ─── Archive Trip ─────────────────────────────────────────────────────────
  ARCHIVE_TRIP_CREATE: 'archive_trip_create',
  ARCHIVE_TRIP_DELETE: 'archive_trip_delete',
  ARCHIVE_TRIP_UPDATE: 'archive_trip_update',
  ARCHIVE_TRIP_VIEW: 'archive_trip_view',
  ARCHIVE_POST_VERIFY_OPERATION: 'archive_post_verify_operation',
  ARCHIVE_TRIP_COMPLETION_IMPORT_OPERATION: 'archive_trip_completion_import_operation',
  CLEAR_TRIPS_OPERATION: 'clear_trips_operation',
  TRIP_EXPORT_OPERATION: 'trip_export_operation',
  TRIP_REVIEW_OPERATION: 'trip_review_operation',

  // ─── Archive Vehicle Event ────────────────────────────────────────────────
  ARCHIVE_VEHICLE_EVENT_VIEW: 'archive_vehicle_event_view',

  // ─── Batch Job ────────────────────────────────────────────────────────────
  BATCH_JOB_CREATE: 'batch_job_create',
  BATCH_JOB_DELETE: 'batch_job_delete',
  BATCH_JOB_UPDATE: 'batch_job_update',
  BATCH_JOB_VIEW: 'batch_job_view',

  // ─── Billing Code Set ─────────────────────────────────────────────────────
  BILLING_CODE_SET_CREATE: 'billing_code_set_create',
  BILLING_CODE_SET_DELETE: 'billing_code_set_delete',
  BILLING_CODE_SET_UPDATE: 'billing_code_set_update',
  BILLING_CODE_SET_VIEW: 'billing_code_set_view',

  // ─── Calendar ─────────────────────────────────────────────────────────────
  CALENDAR_CREATE: 'calendar_create',
  CALENDAR_DELETE: 'calendar_delete',
  CALENDAR_UPDATE: 'calendar_update',
  CALENDAR_VIEW: 'calendar_view',

  // ─── Canned Message ───────────────────────────────────────────────────────
  CANNED_MESSAGE_CREATE: 'canned_message_create',
  CANNED_MESSAGE_DELETE: 'canned_message_delete',
  CANNED_MESSAGE_UPDATE: 'canned_message_update',
  CANNED_MESSAGE_VIEW: 'canned_message_view',

  // ─── Client / Customer Pool ───────────────────────────────────────────────
  CLIENT_EXPORT_OPERATION: 'client_export_operation',
  CLIENT_POOL_CREATE: 'client_pool_create',
  CLIENT_POOL_DELETE: 'client_pool_delete',
  CLIENT_POOL_UPDATE: 'client_pool_update',
  CLIENT_POOL_VIEW: 'client_pool_view',

  // ─── Company / Provider ───────────────────────────────────────────────────
  COMPANY_BLACKLIST_CREATE: 'company_blacklist_create',
  COMPANY_BLACKLIST_DELETE: 'company_blacklist_delete',
  COMPANY_BLACKLIST_UPDATE: 'company_blacklist_update',
  COMPANY_BLACKLIST_VIEW: 'company_blacklist_view',
  COMPANY_CREATE: 'company_create',
  COMPANY_DELETE: 'company_delete',
  COMPANY_TRIP_LIMIT_OPERATION: 'company_trip_limit_operation',
  COMPANY_UPDATE: 'company_update',
  COMPANY_VIEW: 'company_view',

  // ─── Configuration ────────────────────────────────────────────────────────
  CONFIGURATION_CREATE: 'configuration_create',
  CONFIGURATION_UPDATE: 'configuration_update',
  CONFIGURATION_VIEW: 'configuration_view',

  // ─── Customer / Client ────────────────────────────────────────────────────
  CUSTOMER_ACCOUNT_TRANSACTION_CREATE: 'customer_account_transaction_create',
  CUSTOMER_ACCOUNT_TRANSACTION_VIEW: 'customer_account_transaction_view',
  CUSTOMER_CREATE: 'customer_create',
  CUSTOMER_DELETE: 'customer_delete',
  CUSTOMER_UPDATE: 'customer_update',
  CUSTOMER_VIEW: 'customer_view',

  // ─── Dashboard ────────────────────────────────────────────────────────────
  DASHBOARD_VIEW: 'dashboard_view',
  INSIGHT_VIEW: 'insight_view',
  NOTIFICATION_VIEW: 'notification_view',

  // ─── Dashboard widgets (BRD 6.2 FR-DB-05 — per-widget RBAC) ──────────────
  // One permission per BRD MVP widget. Backed by K3 migration
  // 20260507100000_add_dashboard_widget_permissions.rb. New rows arrive in
  // /session.permissions[] automatically once the migration runs.
  DASHBOARD_WIDGET_LIVE_MAP_VIEW:                   'dashboard_widget_live_map_view',
  DASHBOARD_WIDGET_OTP_SUMMARY_VIEW:                'dashboard_widget_otp_summary_view',
  DASHBOARD_WIDGET_ALERTS_SUMMARY_VIEW:             'dashboard_widget_alerts_summary_view',
  DASHBOARD_WIDGET_ANTICIPATED_LATE_NEXT_HOUR_VIEW: 'dashboard_widget_anticipated_late_next_hour_view',
  DASHBOARD_WIDGET_ANTICIPATED_LATE_REST_OF_DAY_VIEW: 'dashboard_widget_anticipated_late_rest_of_day_view',
  DASHBOARD_WIDGET_DAILY_STATS_VIEW:                'dashboard_widget_daily_stats_view',
  DASHBOARD_WIDGET_WEEKLY_STATS_VIEW:               'dashboard_widget_weekly_stats_view',
  DASHBOARD_WIDGET_MONTHLY_STATS_VIEW:              'dashboard_widget_monthly_stats_view',
  DASHBOARD_WIDGET_SAME_DAY_BOOKING_VIEW:           'dashboard_widget_same_day_booking_view',
  DASHBOARD_WIDGET_ANTICIPATED_EARLY_NEXT_HOUR_VIEW: 'dashboard_widget_anticipated_early_next_hour_view',
  DASHBOARD_WIDGET_ANTICIPATED_EARLY_REST_OF_DAY_VIEW: 'dashboard_widget_anticipated_early_rest_of_day_view',
  DASHBOARD_WIDGET_STOP_OTP_LIVE_VIEW:              'dashboard_widget_stop_otp_live_view',
  DASHBOARD_WIDGET_LATE_BREAK_START_VIEW:           'dashboard_widget_late_break_start_view',
  DASHBOARD_WIDGET_MANDATORY_TO_RUN_VIEW:           'dashboard_widget_mandatory_to_run_view',
  DASHBOARD_WIDGET_SERVICE_MILES_VIEW:              'dashboard_widget_service_miles_view',
  DASHBOARD_WIDGET_SERVICE_HOURS_VIEW:              'dashboard_widget_service_hours_view',
  DASHBOARD_WIDGET_INSPECTION_FAILURES_VIEW:        'dashboard_widget_inspection_failures_view',
  DASHBOARD_WIDGET_NO_RUNS_VIEW:                    'dashboard_widget_no_runs_view',
  DASHBOARD_WIDGET_ARRIVE_CLICK_COMPLIANCE_VIEW:    'dashboard_widget_arrive_click_compliance_view',
  DASHBOARD_WIDGET_DRIVER_OTP_RANK_VIEW:            'dashboard_widget_driver_otp_rank_view',
  DASHBOARD_WIDGET_IDLE_TIME_VIEW:                  'dashboard_widget_idle_time_view',

  // ─── Device ───────────────────────────────────────────────────────────────
  DEVICE_CREATE: 'device_create',
  DEVICE_DELETE: 'device_delete',
  DEVICE_UPDATE: 'device_update',
  DEVICE_VIEW: 'device_view',

  // ─── Driver ───────────────────────────────────────────────────────────────
  DRIVER_CREATE: 'driver_create',
  DRIVER_DELETE: 'driver_delete',
  DRIVER_LOGOUT_OPERATION: 'driver_logout_operation',
  DRIVER_UPDATE: 'driver_update',
  DRIVER_VIEW: 'driver_view',

  // ─── Driver Addon ─────────────────────────────────────────────────────────
  DRIVER_ADDON_CREATE: 'driver_addon_create',
  DRIVER_ADDON_DELETE: 'driver_addon_delete',
  DRIVER_ADDON_UPDATE: 'driver_addon_update',
  DRIVER_ADDON_VIEW: 'driver_addon_view',

  // ─── Driver Incident ──────────────────────────────────────────────────────
  DRIVER_INCIDENT_CREATE: 'driver_incident_create',
  DRIVER_INCIDENT_DELETE: 'driver_incident_delete',
  DRIVER_INCIDENT_UPDATE: 'driver_incident_update',
  DRIVER_INCIDENT_VIEW: 'driver_incident_view',

  // ─── EDI Service ──────────────────────────────────────────────────────────
  EDISERVICE_ACCESS: 'ediservice_access',

  // ─── Enclosure / Polygon ──────────────────────────────────────────────────
  ENCLOSURE_CREATE: 'enclosure_create',
  ENCLOSURE_DELETE: 'enclosure_delete',
  ENCLOSURE_UPDATE: 'enclosure_update',
  ENCLOSURE_VIEW: 'enclosure_view',

  // ─── Expense ──────────────────────────────────────────────────────────────
  EXPENSE_CREATE: 'expense_create',
  EXPENSE_DELETE: 'expense_delete',
  EXPENSE_EXPORT_OPERATION: 'expense_export_operation',
  EXPENSE_UPDATE: 'expense_update',
  EXPENSE_VIEW: 'expense_view',

  // ─── Export File ──────────────────────────────────────────────────────────
  EXPORT_FILE_VIEW: 'export_file_view',

  // ─── Fare Billing Code ────────────────────────────────────────────────────
  FARE_BILLING_CODE_CREATE: 'fare_billing_code_create',
  FARE_BILLING_CODE_DELETE: 'fare_billing_code_delete',
  FARE_BILLING_CODE_UPDATE: 'fare_billing_code_update',
  FARE_BILLING_CODE_VIEW: 'fare_billing_code_view',

  // ─── Feedback / Service Record ────────────────────────────────────────────
  FEEDBACK_CREATE: 'feedback_create',
  FEEDBACK_DELETE: 'feedback_delete',
  FEEDBACK_UPDATE: 'feedback_update',
  FEEDBACK_VIEW: 'feedback_view',

  FEEDBACK_ISSUE_CREATE: 'feedback_issue_create',
  FEEDBACK_ISSUE_DELETE: 'feedback_issue_delete',
  FEEDBACK_ISSUE_UPDATE: 'feedback_issue_update',
  FEEDBACK_ISSUE_VIEW: 'feedback_issue_view',

  FEEDBACK_LIQUIDATED_DAMAGE_CREATE: 'feedback_liquidated_damage_create',
  FEEDBACK_LIQUIDATED_DAMAGE_DELETE: 'feedback_liquidated_damage_delete',
  FEEDBACK_LIQUIDATED_DAMAGE_UPDATE: 'feedback_liquidated_damage_update',
  FEEDBACK_LIQUIDATED_DAMAGE_VIEW: 'feedback_liquidated_damage_view',

  FEEDBACK_LOG_ENTRY_CREATE: 'feedback_log_entry_create',
  FEEDBACK_LOG_ENTRY_DELETE: 'feedback_log_entry_delete',
  FEEDBACK_LOG_ENTRY_UPDATE: 'feedback_log_entry_update',
  FEEDBACK_LOG_ENTRY_VIEW: 'feedback_log_entry_view',

  // ─── Funding Source ───────────────────────────────────────────────────────
  FUNDING_SOURCE_CREATE: 'funding_source_create',
  FUNDING_SOURCE_DELETE: 'funding_source_delete',
  FUNDING_SOURCE_UPDATE: 'funding_source_update',
  FUNDING_SOURCE_VIEW: 'funding_source_view',

  // ─── GIS Address ──────────────────────────────────────────────────────────
  GIS_ADDRESS_UPDATE: 'gis_address_update',
  GIS_ADDRESS_VIEW: 'gis_address_view',

  // ─── Inspection Checklist Template ────────────────────────────────────────
  INSPECTION_CHECKLIST_TEMPLATE_CREATE: 'inspection_checklist_template_create',
  INSPECTION_CHECKLIST_TEMPLATE_DELETE: 'inspection_checklist_template_delete',
  INSPECTION_CHECKLIST_TEMPLATE_UPDATE: 'inspection_checklist_template_update',
  INSPECTION_CHECKLIST_TEMPLATE_VIEW: 'inspection_checklist_template_view',

  // ─── Location ─────────────────────────────────────────────────────────────
  LOCATION_CREATE: 'location_create',
  LOCATION_DELETE: 'location_delete',
  LOCATION_UPDATE: 'location_update',
  LOCATION_VIEW: 'location_view',

  // ─── MDT Device ───────────────────────────────────────────────────────────
  MDT_DEVICE_CREATE: 'mdt_device_create',
  MDT_DEVICE_DELETE: 'mdt_device_delete',
  MDT_DEVICE_UPDATE: 'mdt_device_update',
  MDT_DEVICE_VIEW: 'mdt_device_view',

  // ─── Metaroute / Schedule ─────────────────────────────────────────────────
  METAROUTE_CUSTOM_FIELD_CREATE: 'metaroute_custom_field_create',
  METAROUTE_CUSTOM_FIELD_DELETE: 'metaroute_custom_field_delete',
  METAROUTE_CUSTOM_FIELD_UPDATE: 'metaroute_custom_field_update',
  METAROUTE_CUSTOM_FIELD_VIEW: 'metaroute_custom_field_view',
  METAROUTE_MARK_COMPLETE_OPERATION: 'metaroute_mark_complete_operation',
  METAROUTE_OPTIMIZE_OPERATION: 'metaroute_optimize_operation',
  LOCK_METAROUTES: 'lock_metaroutes',
  METAROUTE_UPDATE: 'metaroute_update',
  METAROUTE_VIEW: 'metaroute_view',
  STOP_ORDER_EDIT_OPERATION: 'stop_order_edit_operation',

  // ─── Order Import ─────────────────────────────────────────────────────────
  ORDERIMPORT_ACCESS: 'orderimport_access',

  // ─── Provider Rate ────────────────────────────────────────────────────────
  PROVIDER_RATE_CREATE: 'provider_rate_create',
  PROVIDER_RATE_DELETE: 'provider_rate_delete',
  PROVIDER_RATE_UPDATE: 'provider_rate_update',
  PROVIDER_RATE_VIEW: 'provider_rate_view',

  // ─── Purpose ──────────────────────────────────────────────────────────────
  PURPOSE_CREATE: 'purpose_create',
  PURPOSE_DELETE: 'purpose_delete',
  PURPOSE_UPDATE: 'purpose_update',
  PURPOSE_VIEW: 'purpose_view',

  // ─── Reimbursement ────────────────────────────────────────────────────────
  REIMBURSEMENT_APPROVE_DENY_OPERATION: 'reimbursement_approve_deny_operation',
  REIMBURSEMENT_CREATE: 'reimbursement_create',
  REIMBURSEMENT_DELETE: 'reimbursement_delete',
  REIMBURSEMENT_DISBURSEMENT_OPERATION: 'reimbursement_disbursement_operation',
  REIMBURSEMENT_EXPORT_OPERATION: 'reimbursement_export_operation',
  REIMBURSEMENT_SPLIT_OPERATION: 'reimbursement_split_operation',
  REIMBURSEMENT_UPDATE: 'reimbursement_update',
  REIMBURSEMENT_VIEW: 'reimbursement_view',
  ROLLBACK_TO_IN_REVIEW_OPERATION: 'rollback_to_in_review_operation',
  VERIFY_REIMBURSEMENT_EXPENSES_OPERATION: 'verify_reimbursement_expenses_operation',

  // ─── Report Template ──────────────────────────────────────────────────────
  REPORT_TEMPLATE_UPDATE: 'report_template_update',
  REPORT_TEMPLATE_VIEW: 'report_template_view',

  // ─── Reporting ────────────────────────────────────────────────────────────
  REPORTING_ACCESS: 'reporting_access',
  REPORTING_ADHOC_ACCESS: 'reporting_adhoc_access',
  REPORTING_ADMIN_OPERATION: 'reporting_admin_operation',

  // ─── Required Field ───────────────────────────────────────────────────────
  REQUIRED_FIELD_UPDATE: 'required_field_update',
  REQUIRED_FIELD_VIEW: 'required_field_view',

  // ─── Rider Policy Violation ───────────────────────────────────────────────
  RIDER_POLICY_VIOLATION_CREATE: 'rider_policy_violation_create',
  RIDER_POLICY_VIOLATION_DELETE: 'rider_policy_violation_delete',
  RIDER_POLICY_VIOLATION_UPDATE: 'rider_policy_violation_update',
  RIDER_POLICY_VIOLATION_VIEW: 'rider_policy_violation_view',

  // ─── Road Call ────────────────────────────────────────────────────────────
  ROADCALL_CREATE: 'roadcall_create',
  ROADCALL_DELETE: 'roadcall_delete',
  ROADCALL_UPDATE: 'roadcall_update',
  ROADCALL_VIEW: 'roadcall_view',

  // ─── Route Event ──────────────────────────────────────────────────────────
  ROUTE_EVENT_VIEW: 'route_event_view',

  // ─── Run ──────────────────────────────────────────────────────────────────
  RUN_CREATE: 'run_create',
  RUN_DELETE: 'run_delete',
  RUN_UPDATE: 'run_update',
  RUN_VIEW: 'run_view',

  // ─── Run Availability ─────────────────────────────────────────────────────
  RUN_AVAILABILITY_CREATE: 'run_availability_create',
  RUN_AVAILABILITY_DELETE: 'run_availability_delete',
  RUN_AVAILABILITY_SYNC_OPERATION: 'run_availability_sync_operation',
  RUN_AVAILABILITY_UPDATE_FUTURE: 'run_availability_update_future',
  RUN_AVAILABILITY_UPDATE_TODAY: 'run_availability_update_today',

  // ─── Run Pool ─────────────────────────────────────────────────────────────
  RUN_POOL_CREATE: 'run_pool_create',
  RUN_POOL_DELETE: 'run_pool_delete',
  RUN_POOL_UPDATE: 'run_pool_update',
  RUN_POOL_VIEW: 'run_pool_view',

  // ─── Self-Service User ────────────────────────────────────────────────────
  SELFSERVICE_USER_CREATE: 'selfservice_user_create',
  SELFSERVICE_USER_DELETE: 'selfservice_user_delete',
  SELFSERVICE_USER_UPDATE: 'selfservice_user_update',
  SELFSERVICE_USER_VIEW: 'selfservice_user_view',

  // ─── Service Area ─────────────────────────────────────────────────────────
  SERVICE_AREA_CREATE: 'service_area_create',
  SERVICE_AREA_DELETE: 'service_area_delete',
  SERVICE_AREA_UPDATE: 'service_area_update',
  SERVICE_AREA_VIEW: 'service_area_view',

  // ─── Subscription ─────────────────────────────────────────────────────────
  SUBSCRIPTION_CANCEL_OPERATION: 'subscription_cancel_operation',
  SUBSCRIPTION_CREATE: 'subscription_create',
  SUBSCRIPTION_UPDATE: 'subscription_update',
  SUBSCRIPTION_VIEW: 'subscription_view',

  // ─── Surcharge ────────────────────────────────────────────────────────────
  SURCHARGE_CREATE: 'surcharge_create',
  SURCHARGE_DELETE: 'surcharge_delete',
  SURCHARGE_UPDATE: 'surcharge_update',
  SURCHARGE_VIEW: 'surcharge_view',

  // ─── Template Route / Fixed Route ─────────────────────────────────────────
  TEMPLATE_ROUTE_CREATE: 'template_route_create',
  TEMPLATE_ROUTE_DELETE: 'template_route_delete',
  TEMPLATE_ROUTE_UPDATE: 'template_route_update',
  TEMPLATE_ROUTE_VIEW: 'template_route_view',

  // ─── Trip ─────────────────────────────────────────────────────────────────
  TRIP_ACCEPT_REJECT_OPERATION: 'trip_accept_reject_operation',
  TRIP_CALCULATE_WINDOW_OPERATION: 'trip_calculate_window_operation',
  TRIP_CANCEL_OPERATION: 'trip_cancel_operation',
  TRIP_CHANGE_PROVIDER_OPERATION: 'trip_change_provider_operation',
  TRIP_CHANGE_TARGET_RUNS_OPERATION: 'trip_change_target_runs_operation',
  TRIP_COMPLETION_EXPORT_OPERATION: 'trip_completion_export_operation',
  TRIP_CREATE: 'trip_create',
  TRIP_GROUP_OPERATION: 'trip_group_operation',
  TRIP_LOCK_OPERATION: 'trip_lock_operation',
  TRIP_LOCK_PROVIDER_OPERATION: 'trip_lock_provider_operation',
  TRIP_PRE_VERIFY_OPERATION: 'trip_pre_verify_operation',
  TRIP_REASSIGN_OPERATION: 'trip_reassign_operation',
  TRIP_RESCHEDULE_OPERATION: 'trip_reschedule_operation',
  TRIP_SHIFT_OPERATION: 'trip_shift_operation',
  TRIP_UPDATE: 'trip_update',
  TRIP_VIEW: 'trip_view',
  TRIP_VIOLATION_OPERATION: 'trip_violation_operation',
  TRIP_WILLCALL_HANDLE_OPERATION: 'trip_willcall_handle_operation',

  // ─── Trip Event ───────────────────────────────────────────────────────────
  TRIP_EVENT_VIEW: 'trip_event_view',

  // ─── Trip Export Profile ──────────────────────────────────────────────────
  TRIP_EXPORT_PROFILE_CREATE: 'trip_export_profile_create',
  TRIP_EXPORT_PROFILE_DELETE: 'trip_export_profile_delete',
  TRIP_EXPORT_PROFILE_UPDATE: 'trip_export_profile_update',
  TRIP_EXPORT_PROFILE_VIEW: 'trip_export_profile_view',

  // ─── Trip Group ───────────────────────────────────────────────────────────
  TRIP_GROUP_CREATE: 'trip_group_create',
  TRIP_GROUP_DELETE: 'trip_group_delete',
  TRIP_GROUP_OPTIMIZE_OPERATION: 'trip_group_optimize_operation',
  TRIP_GROUP_UPDATE: 'trip_group_update',
  TRIP_GROUP_VIEW: 'trip_group_view',

  // ─── User ─────────────────────────────────────────────────────────────────
  USER_CREATE: 'user_create',
  USER_DELETE: 'user_delete',
  USER_UPDATE: 'user_update',
  USER_VIEW: 'user_view',

  // ─── User Event ───────────────────────────────────────────────────────────
  USER_EVENT_VIEW: 'user_event_view',

  // ─── User Filter ──────────────────────────────────────────────────────────
  USER_FILTER_CREATE: 'user_filter_create',
  USER_FILTER_DELETE: 'user_filter_delete',
  USER_FILTER_UPDATE: 'user_filter_update',
  USER_FILTER_VIEW: 'user_filter_view',

  // ─── User Role ────────────────────────────────────────────────────────────
  USER_ROLE_CREATE: 'user_role_create',
  USER_ROLE_DELETE: 'user_role_delete',
  USER_ROLE_UPDATE: 'user_role_update',
  USER_ROLE_VIEW: 'user_role_view',

  // ─── Vehicle ──────────────────────────────────────────────────────────────
  VEHICLE_ASSIGN_OPERATION: 'vehicle_assign_operation',
  VEHICLE_CREATE: 'vehicle_create',
  VEHICLE_DELETE: 'vehicle_delete',
  VEHICLE_MESSAGE_OPERATION: 'vehicle_message_operation',
  VEHICLE_UPDATE: 'vehicle_update',
  VEHICLE_VIEW: 'vehicle_view',

  // ─── Vehicle Addon ────────────────────────────────────────────────────────
  VEHICLE_ADDON_CREATE: 'vehicle_addon_create',
  VEHICLE_ADDON_DELETE: 'vehicle_addon_delete',
  VEHICLE_ADDON_UPDATE: 'vehicle_addon_update',
  VEHICLE_ADDON_VIEW: 'vehicle_addon_view',

  // ─── System-level permissions ─────────────────────────────────────────────
  // These are issued by the DRT backend but have no Dispatcher UI feature
  // surface. Included for completeness so session-level code can inspect them.
  CENTER_PORTAL_ACCESS: 'center_portal_access',
  DRT_ACCESS: 'drt_access',
  ECOLANE_ACCESS: 'ecolane_access',
  // Standalone archive-trip verify grant (distinct from trip_pre_verify and
  // archive_post_verify). Issued to users who can verify archive trips via
  // the backend verify_operation grant.
  VERIFY_OPERATION: 'verify_operation',

} as const;

// ─── Type helpers ─────────────────────────────────────────────────────────────

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

export const isValidPermission = (permission: string): permission is Permission =>
  Object.values(PERMISSIONS).includes(permission as Permission);
